/**
 * Logger utility for Akem platform
 * Handles environment detection and provides consistent logging across frontend and backend
 */
export var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["DEBUG"] = 0] = "DEBUG";
    LogLevel[LogLevel["INFO"] = 1] = "INFO";
    LogLevel[LogLevel["WARN"] = 2] = "WARN";
    LogLevel[LogLevel["ERROR"] = 3] = "ERROR";
})(LogLevel || (LogLevel = {}));
class Logger {
    config;
    isDevelopment;
    constructor(config) {
        // Detect environment - works for both frontend (Vite) and backend (Node.js)
        this.isDevelopment = this.detectDevelopmentMode();
        this.config = {
            level: this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN,
            enableColors: this.isDevelopment,
            enableTimestamps: true,
            ...config,
        };
    }
    detectDevelopmentMode() {
        // Frontend detection - check if we're in a browser environment
        if (typeof globalThis !== 'undefined' && 'window' in globalThis) {
            // Check for Vite development mode
            if (typeof import.meta !== 'undefined' && 'env' in import.meta) {
                const metaEnv = import.meta.env;
                return metaEnv && metaEnv.DEV === true;
            }
            // Fallback for other bundlers
            return true; // Default to development for frontend
        }
        // Backend (Node.js) detection
        if (typeof process !== 'undefined' && process.env) {
            return process.env.NODE_ENV !== 'production';
        }
        // Default to production mode if we can't detect
        return false;
    }
    shouldLog(level) {
        return level >= this.config.level;
    }
    formatMessage(level, message, context) {
        const parts = [];
        // Add timestamp
        if (this.config.enableTimestamps) {
            parts.push(`[${new Date().toISOString()}]`);
        }
        // Add prefix
        if (this.config.prefix) {
            parts.push(`[${this.config.prefix}]`);
        }
        // Add log level
        const levelName = LogLevel[level];
        const isBrowser = typeof globalThis !== 'undefined' && 'window' in globalThis;
        if (this.config.enableColors && isBrowser) {
            // Frontend - just add the placeholder, colors handled in log method
            parts.push(`%c[${levelName}]`);
        }
        else if (this.config.enableColors && typeof process !== 'undefined') {
            // Backend colors using ANSI
            const colors = {
                DEBUG: '\x1b[90m', // Gray
                INFO: '\x1b[34m', // Blue
                WARN: '\x1b[33m', // Yellow
                ERROR: '\x1b[31m', // Red
            };
            parts.push(`${colors[levelName]}[${levelName}]\x1b[0m`);
        }
        else {
            parts.push(`[${levelName}]`);
        }
        // Add context if provided
        if (context) {
            parts.push(`[${context}]`);
        }
        // Add message
        parts.push(message);
        return parts.join(' ');
    }
    log(level, message, data, context) {
        if (!this.shouldLog(level)) {
            return;
        }
        const formattedMessage = this.formatMessage(level, message, context);
        // Frontend color styles
        const frontendColors = {
            [LogLevel.DEBUG]: 'color: #6B7280',
            [LogLevel.INFO]: 'color: #3B82F6',
            [LogLevel.WARN]: 'color: #F59E0B',
            [LogLevel.ERROR]: 'color: #EF4444',
        };
        // Choose appropriate console method
        const consoleMethod = {
            [LogLevel.DEBUG]: console.debug,
            [LogLevel.INFO]: console.info,
            [LogLevel.WARN]: console.warn,
            [LogLevel.ERROR]: console.error,
        }[level];
        const isBrowser = typeof globalThis !== 'undefined' && 'window' in globalThis;
        if (this.config.enableColors && isBrowser) {
            // Frontend with CSS colors
            if (data) {
                consoleMethod(formattedMessage, frontendColors[level], data);
            }
            else {
                consoleMethod(formattedMessage, frontendColors[level]);
            }
        }
        else {
            // Backend or no colors
            if (data) {
                consoleMethod(formattedMessage, data);
            }
            else {
                consoleMethod(formattedMessage);
            }
        }
    }
    /**
     * Log debug information (only in development)
     */
    debug(message, data, context) {
        this.log(LogLevel.DEBUG, message, data, context);
    }
    /**
     * Log general information
     */
    info(message, data, context) {
        this.log(LogLevel.INFO, message, data, context);
    }
    /**
     * Log warnings
     */
    warn(message, data, context) {
        this.log(LogLevel.WARN, message, data, context);
    }
    /**
     * Log errors
     */
    error(message, data, context) {
        this.log(LogLevel.ERROR, message, data, context);
    }
    /**
     * Create a child logger with additional context
     */
    child(context) {
        return new Logger({
            ...this.config,
            prefix: this.config.prefix ? `${this.config.prefix}:${context}` : context,
        });
    }
    /**
     * Set log level dynamically
     */
    setLevel(level) {
        this.config.level = level;
    }
    /**
     * Get current configuration
     */
    getConfig() {
        return { ...this.config };
    }
    /**
     * Check if development mode
     */
    isDev() {
        return this.isDevelopment;
    }
}
// Create default logger instance
export const logger = new Logger();
// Create pre-configured loggers for common use cases
export const apiLogger = logger.child('API');
export const uiLogger = logger.child('UI');
export const configLogger = logger.child('CONFIG');
export const healthLogger = logger.child('HEALTH');
// Export logger class for custom instances
export { Logger };
